import React from 'react'
import { observer } from 'mobx-react'

import { ChargeOrRefundDialogViewModel } from './ChargeOrRefundDialogViewModel'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
  ModalSheetTitle,
} from 'components/ModalSheet'
import {
  Form,
  FormGroupContainer,
  FormRow,
  NumericInputContainer,
  TextAreaContainer,
} from 'components/inputs'
import {
  CustomSelect,
  ItemSource,
  useCustomSelectStateFromItemSource,
} from 'components/inputs/CustomSelect'
import Icon from 'components/Icon'
import Button from 'components/Button'

import styles from './ChargeOrRefundDialog.sass'
import ErrorBlock from 'components/ErrorBlock'
import { RadioButtonGroup, RadioButtonItem } from 'components/RadioButtonGroup'

interface ChargeOrRefundDialogProps {
  vm: ChargeOrRefundDialogViewModel
}

export default observer(function ChargeOrRefundDialog({
  vm,
}: ChargeOrRefundDialogProps) {
  const close = vm.submit.pending ? undefined : vm.close

  const paymentMethodSelectProps = useCustomSelectStateFromItemSource({
    itemSource: React.useMemo(
      () =>
        ItemSource.static(vm.availableBillingMethodOptions, (item, text) =>
          item.displayLabel.includes(text.toLowerCase())
        ),
      [vm.availableBillingMethodOptions]
    ),
  })

  return (
    <ModalSheet
      showing={vm.showing}
      disableCloseOnEsc
      disableCloseOnOutsideClick
      onRequestClose={close}
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>Charge or refund this job</ModalSheetTitle>
      </ModalSheetHeader>

      <ModalSheetContent>
        <Form data-test="charge-or-refund-dialog-form">
          <ModalSheetSection spaced>
            <FormRow>
              <FormGroupContainer>
                <div className={styles.label}>What do you want to do?</div>
                <RadioButtonGroup size="big-wide">
                  <RadioButtonItem
                    onClick={() => vm.onChangeMode('charge')}
                    active={vm.mode === 'charge'}
                  >
                    Charge
                  </RadioButtonItem>
                  <RadioButtonItem
                    onClick={() => vm.onChangeMode('refund')}
                    dataTest="charge-or-refund-dialog-refund-button"
                    active={vm.mode === 'refund'}
                  >
                    Refund
                  </RadioButtonItem>
                </RadioButtonGroup>
              </FormGroupContainer>
            </FormRow>

            {vm.mode === 'charge' && (
              <FormRow>
                <FormGroupContainer
                  label="Billing Method"
                  error={vm.validation.getError('billingMethod')}
                >
                  <CustomSelect
                    {...paymentMethodSelectProps}
                    multi={false}
                    icon={<Icon name="dropdown" />}
                    itemToString={(i) => String(i?.displayLabel)}
                    placeholder="Billing Method"
                    value={vm.billingMethod ?? undefined}
                    status={
                      vm.validation.getError('billingMethod')
                        ? 'danger'
                        : 'default'
                    }
                    onChange={vm.setBillingMethod}
                  />
                </FormGroupContainer>
              </FormRow>
            )}

            <FormRow>
              <NumericInputContainer
                label="Amount"
                placeholder="0.00"
                help={`How much do you want to ${vm.mode}?`}
                error={vm.validation.getError('amount')}
                model={vm}
                field="amount"
                status={vm.validation.getError('amount') ? 'danger' : 'default'}
                dataTest="charge-or-refund-dialog-amount"
                precision={2}
              />
            </FormRow>

            <FormRow>
              <TextAreaContainer
                help={`Why are you ${
                  vm.mode === 'charge' ? 'charging' : 'refunding'
                } this job`}
                error={vm.validation.getError('reason')}
                model={vm}
                field="reason"
                name="reason"
                label="Reason"
                data-test="charge-or-refund-dialog-reason"
                placeholder="Provide a reason"
              />
            </FormRow>
          </ModalSheetSection>
        </Form>
      </ModalSheetContent>

      {vm.submit.rejected && (
        <ErrorBlock
          error={vm.submit.error || 'Please check your input and try again.'}
          onCloseClick={vm.submit.reset}
          tackOn="bottom"
        />
      )}

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button type="default" onClick={vm.close}>
            Close
          </Button>
          <Button
            type="primary"
            onClick={vm.submit}
            loading={vm.submit.pending}
            disabled={vm.submit.pending || vm.disabled}
            data-test="charge-or-refund-dialog-submit"
          >
            Submit
          </Button>
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
})
